import { useEffect, useState } from 'react';
import { PHONE_VIEW_QUERY } from '@/lib/media';

/** The room a delegation popover keeps from the screen's edges (Radix `collisionPadding`). */
const EDGE = 12;
/** How often the held toasts are measured while a popover is open: sonner moves them with transforms. */
const EVERY_MS = 200;

const phone = (): boolean => typeof window.matchMedia === 'function' && window.matchMedia(PHONE_VIEW_QUERY).matches;

/** How far up from the bottom edge the visible toasts reach, plus a gap; the plain edge when there are none. */
function bottomRoom(): number {
  let top = Infinity;
  for (const toast of document.querySelectorAll<HTMLElement>('[data-sonner-toast]')) {
    if (toast.dataset.removed === 'true') continue;
    const box = toast.getBoundingClientRect();
    if (box.height > 0 && box.top < window.innerHeight) top = Math.min(top, box.top);
  }
  return top === Infinity ? EDGE : Math.max(EDGE, Math.ceil(window.innerHeight - top) + 8);
}

/**
 * D119: on a phone the toast stack is a full-width strip over the bottom edge, and the "Hoàn tác" toast is held while
 * a card is open (lib/undo-toast.tsx). An open delegation popover keeps clear of it — its bottom collision padding is
 * the height the toasts take — so its footer (sticky, inside the popover's available height) stays tappable. The hold
 * itself is untouched. On a desktop the stack sits in a corner and the padding is the plain edge.
 */
export function useToastClearance(open: boolean) {
  const [bottom, setBottom] = useState(EDGE);
  useEffect(() => {
    if (!open || !phone()) { setBottom(EDGE); return undefined; }
    const measure = () => setBottom(bottomRoom());
    measure();
    const timer = window.setInterval(measure, EVERY_MS);
    return () => window.clearInterval(timer);
  }, [open]);
  return { top: EDGE, right: EDGE, bottom, left: EDGE };
}
